import type { EventView, ScoreView } from "../api/types.js";

/**
 * 読み込み中。
 *
 * 開始の合図で全員が一斉にQRコードを読み取ると、参加受付が混み合うことがある。
 * その間も自動的に再試行しているため、参加者が操作をやり直す必要はない。
 * 何も知らせないと「固まった」と受け取られるので、待っている理由を表示する。
 */
export function LoadingScreen({ busy = false }: { busy?: boolean } = {}): React.ReactElement {
  return (
    <div className="centered">
      <p className="centered__text">
        {busy ? "混み合っています。自動で再試行しています…" : "読み込んでいます…"}
      </p>
      {busy && (
        <p className="centered__note">
          そのままお待ちください。操作は不要です。
        </p>
      )}
    </div>
  );
}

/**
 * 参加できなかったときの画面。
 * 失敗理由は区別せず、運営に声をかけてもらう導線だけを示す。
 */
export function BlockedScreen({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}): React.ReactElement {
  return (
    <div className="centered">
      <div className="centered__box">
        <p className="centered__icon" aria-hidden="true">
          !
        </p>
        <p className="centered__text">{message}</p>
        {onRetry && (
          <button type="button" className="btn btn--primary btn--block" onClick={onRetry}>
            もう一度試す
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * 受付完了・待機（P-03）。
 *
 * 受付は済んだがゲームはまだ始まっていない段階。運営が全員の参加を確認してから
 * 開始するため、その間ここで待つ。開始の配信を受けると自動でゲーム画面へ移る。
 */
export function WaitingScreen({
  event,
  displayName,
  teamName,
}: {
  event: EventView;
  displayName: string | null;
  teamName: string | null;
}): React.ReactElement {
  return (
    <div className="centered">
      <div className="centered__box">
        <p className="finished__event">{event.name}</p>
        <h1 className="waiting__title">
          ようこそ{displayName === null ? "" : `${displayName}さん`}
        </h1>
        {teamName !== null && <p className="finished__owner">{teamName}</p>}
        <p className="centered__text">参加受付が完了しました。</p>
        <p className="centered__text">ゲーム開始まで少々お待ちください。</p>
        <p className="centered__note">
          この画面のままお待ちください。開始すると自動的に切り替わります。
        </p>
      </div>
    </div>
  );
}

/**
 * 一時停止（P-08）。
 *
 * 運営がアナウンスなどで解答受付を止めている間に表示する。
 * 再開の配信を受けると自動でゲーム画面へ戻る。
 */
export function PausedScreen({
  event,
  score,
  ownerName,
}: {
  event: EventView;
  score: ScoreView;
  ownerName: string;
}): React.ReactElement {
  return (
    <div className="centered">
      <div className="centered__box">
        <p className="finished__event">{event.name}</p>
        <h1 className="waiting__title">一時停止中</h1>
        <p className="centered__text">現在、解答の受付を停止しています。</p>
        <p className="centered__text">少々お待ちください。</p>
        <p className="finished__owner">
          {ownerName} ・ 現在 {score.displayScore}点
        </p>
        {/* 停止中は計時も止まっている。残り時間が減らないことを見せて安心してもらう */}
        {event.remainingSec !== null && (
          <p className="centered__note">
            残り時間 {Math.floor(event.remainingSec / 60)}分 は停止しています。
          </p>
        )}
        <p className="centered__note">再開すると自動的に切り替わります。</p>
      </div>
    </div>
  );
}

/** ゲーム終了後の画面（P-09） */
export function FinishedScreen({
  event,
  score,
  ownerName,
}: {
  event: EventView;
  score: ScoreView;
  ownerName: string;
}): React.ReactElement {
  return (
    <div className="centered">
      <div className="centered__box">
        <p className="finished__event">{event.name}</p>
        <h1 className="finished__title">おつかれさまでした</h1>
        <p className="finished__owner">{ownerName}</p>
        <p className="finished__score">
          <span className="finished__score-value">{score.displayScore}</span>
          <span className="finished__score-unit">点</span>
        </p>
        <p className="finished__stats">
          正解 {score.solvedCount} ・ ヒント {score.hintCount} ・ 誤答 {score.wrongCount}
        </p>
        {/* 最終順位はここでは出さない。表彰の場で発表するため（基本設計書 5.3） */}
        <p className="finished__note">結果の発表をお待ちください。</p>
      </div>
    </div>
  );
}
